import React, {
    useState, useMemo, useCallback, forwardRef, useImperativeHandle, Ref, useEffect,
} from 'react';
import {
    ColumnDef, flexRender, getCoreRowModel, SortingState, useReactTable,
} from '@tanstack/react-table';
import {
    DndContext, closestCenter, KeyboardSensor, PointerSensor, useSensor, useSensors, DragEndEvent,
} from '@dnd-kit/core';
import { arrayMove, SortableContext, horizontalListSortingStrategy } from '@dnd-kit/sortable';
import './DraggableTable.scss';
import { NSButton } from 'bricks';
import { DealTypeToId } from 'constants/deals';
import { UnitType, SELECT_COLUMN_ID, ACTIONS_COLUMN_ID } from 'views/Deals/constants';
import NSTable from '../../bricks/NSTable/NSTable';
import { DraggableHeader } from './DraggableHeader/DraggableHeader';
import { ExtendedColumnDef } from './types';

interface DraggableTableProps<TData> {
    data: TData[];
    columns: ExtendedColumnDef<TData>[];
    columnVisibility: Record<string, boolean>;
    selectedItem?: TData & { id: number };
    onAscendingSortPressed?: (columnName: keyof TData) => void;
    onDescendingSortPressed?: (columnName: keyof TData) => void;
    onRowClick?: (row: TData) => void;
    onCreateGroupFromSelection?: (selectedRows: TData[]) => void;
    onAddToExistingGroup?: (selectedRows: TData[]) => void;
    onCreateReport?: (selectedRows: TData[]) => void;
    onDelete?: (selectedRows: TData[]) => void;
    initialColumnOrder?: string[];
    onColumnOrderChange?: (newOrder: string[]) => void;
    sorting: SortingState;
    setSorting: React.Dispatch<React.SetStateAction<SortingState>>;
    selectedUnitType: UnitType;
    handleSelectUnitType: (unitType: UnitType) => void;
}

interface DraggableTableRef {
    resetRowSelection: VoidFunction;
}

const DraggableTable = <TData extends object>(
    {
        data,
        columns,
        columnVisibility,
        onAscendingSortPressed,
        onDescendingSortPressed,
        selectedItem,
        onRowClick,
        onCreateGroupFromSelection,
        onAddToExistingGroup,
        onCreateReport,
        onDelete,
        initialColumnOrder,
        onColumnOrderChange,
        sorting,
        setSorting,
        selectedUnitType,
        handleSelectUnitType,
    }: DraggableTableProps<TData>,
    ref: Ref<DraggableTableRef>,
) => {
    const getInitialColumnOrder = (columns: (ColumnDef<TData> & {
        accessorKey: string;
    })[], initialColumnOrder?: string[]) => {
        const columnsAccessKeys = columns.map(col => col.accessorKey);

        if (initialColumnOrder) {
            const filteredOrder = initialColumnOrder.filter(
                key => key !== SELECT_COLUMN_ID && key !== ACTIONS_COLUMN_ID,
            );

            const missingColumns = columnsAccessKeys.filter(
                key => key !== SELECT_COLUMN_ID
                       && key !== ACTIONS_COLUMN_ID
                       && !initialColumnOrder.includes(key),
            ).filter(Boolean);

            // Insert each new column after its previous sibling in default order
            const result = [...filteredOrder];

            missingColumns.forEach(missingCol => {
                const defaultIndex = columnsAccessKeys.indexOf(missingCol);

                // Find the previous column in default order that exists in result
                let insertAfterIndex = -1;
                for (let i = defaultIndex - 1; i >= 0; i--) {
                    const prevCol = columnsAccessKeys[i];
                    const existingIndex = result.indexOf(prevCol);
                    if (existingIndex !== -1) {
                        insertAfterIndex = existingIndex;
                        break;
                    }
                }

                // Insert after the previous column, or at the end if no previous column found
                if (insertAfterIndex === -1) {
                    result.push(missingCol);
                } else {
                    result.splice(insertAfterIndex + 1, 0, missingCol);
                }
            });

            return [SELECT_COLUMN_ID, ...result, ACTIONS_COLUMN_ID];
        }

        return columnsAccessKeys;
    };

    const memoizedInitialOrder = useMemo(
        () => getInitialColumnOrder(columns, initialColumnOrder),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [columns, initialColumnOrder],
    );

    const [columnOrder, setColumnOrder] = useState(memoizedInitialOrder);

    const table = useReactTable({
        data,
        columns,
        state: {
            columnOrder,
            sorting,
            columnVisibility,
        },
        manualSorting: true,
        onSortingChange: updaterOrValue => setSorting(prev => (typeof updaterOrValue === 'function'
            ? updaterOrValue(prev)
            : updaterOrValue)),
        getCoreRowModel: getCoreRowModel(),
        onColumnOrderChange: setColumnOrder,
    });

    useImperativeHandle(ref, () => ({
        resetRowSelection: () => {
            table.setRowSelection({});
        },
    }));

    const { rows } = table.getSelectedRowModel();
    const selectedRows = useMemo(() => rows.map(row => row.original), [rows]);
    const selectedRowsCount = selectedRows.length;

    const canCreateReport = useMemo(() => selectedRows.every(row => {
        const { dealType, hasSubstantiallyCompleteProForma } = row as {
                dealType: { id: number; name: string };
                hasSubstantiallyCompleteProForma: boolean;
            };
        return dealType.id === DealTypeToId.Development && hasSubstantiallyCompleteProForma;
    }), [selectedRows]);

    useEffect(() => {
        if (sorting && sorting.length > 0) {
            const currentSort = sorting[0];

            if (!currentSort.desc && onAscendingSortPressed) {
                onAscendingSortPressed(currentSort.id as keyof TData);
            } else if (currentSort.desc && onDescendingSortPressed) {
                onDescendingSortPressed(currentSort.id as keyof TData);
            }
        }
    }, [sorting, onAscendingSortPressed, onDescendingSortPressed]);

    const handleSort = (columnId: keyof TData, direction: 'asc' | 'desc') => {
        const newSorting = [{ id: columnId as string, desc: direction === 'desc' }];

        setSorting(newSorting);
    };

    const handleDragEnd = (event: DragEndEvent) => {
        const { active, over } = event;

        if (active && over && active.id !== over.id) {
            setColumnOrder(prevOrder => {
                const oldIndex = prevOrder.indexOf(active.id as string);
                const newIndex = prevOrder.indexOf(over.id as string);

                const newOrder = arrayMove(prevOrder, oldIndex, newIndex);
                if (onColumnOrderChange) {
                    onColumnOrderChange(newOrder);
                }
                return newOrder;
            });
        }
    };

    const RowActions = (
        <div className="DraggableTable__row-actions p-3 d-flex align-items-center">
            <h5>
                {selectedRowsCount}
                {' '}
                selected
            </h5>
            <NSButton
                data-testid="create-group-button"
                className="btn btn-outline-primary m-2 heap-deal-pipeline-bulk-create-group"
                callback={() => onCreateGroupFromSelection?.(selectedRows)}
            >
                Create group from selection
            </NSButton>
            <NSButton
                dataTestId="add-to-existing-group-button"
                className="btn btn-outline-primary m-2 heap-deal-pipeline-bulk-add-to-group"
                callback={() => onAddToExistingGroup?.(selectedRows)}
            >
                Add to existing group
            </NSButton>
            <NSButton
                dataTestId="create-report-button"
                id="create-report-button"
                className="btn btn-outline-primary m-2 heap-deal-pipeline-bulk-create-report"
                callback={() => onCreateReport?.(selectedRows)}
                disabled={!canCreateReport}
                disabledTooltip="Reports are not supported by selected deal types"
            >
                Create report
            </NSButton>
            <NSButton
                dataTestId="delete-button"
                className="btn btn-outline-danger m-2 heap-deal-pipeline-bulk-delete"
                callback={() => onDelete?.(selectedRows)}
            >
                Delete
            </NSButton>
        </div>
    );

    const getSort = useCallback((sorting: SortingState) => (sorting[0]?.desc ? 'desc' : 'asc'), []);

    return (
        <div className="DraggableTable">
            {selectedRowsCount > 0 && RowActions}
            <div className="DraggableTable__container">
                <DndContext
                    sensors={useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 8 } }), useSensor(KeyboardSensor))}
                    collisionDetection={closestCenter}
                    onDragEnd={handleDragEnd}
                >
                    <NSTable className="DraggableTable__table">
                        <thead className="DraggableTable__table__table-header">
                            {table.getHeaderGroups().map(headerGroup => (
                                <tr key={headerGroup.id}>
                                    <SortableContext
                                        items={headerGroup.headers.filter(header => header.column.getCanSort()).map(header => header.id)}
                                        strategy={horizontalListSortingStrategy}
                                    >
                                        {headerGroup.headers.map(header => (header.isPlaceholder ? null : (
                                            <DraggableHeader<TData>
                                                key={header.id}
                                                header={header}
                                                sortedColumnId={sorting[0]?.id ?? null}
                                                sortedDirection={sorting[0]?.id === header.column.id ? getSort(sorting) : null}
                                                onAscendingSortPressed={colId => handleSort(colId as keyof TData, 'asc')}
                                                onDescendingSortPressed={colId => handleSort(colId as keyof TData, 'desc')}
                                                selectedUnitType={selectedUnitType}
                                                handleSelectUnitType={handleSelectUnitType}
                                            />
                                        )))}
                                    </SortableContext>
                                </tr>
                            ))}
                        </thead>
                        <tbody>
                            {table.getRowModel().rows.map(row => (
                                <tr
                                    key={row.id}
                                    className={`NSTable__tbody__tr--level-1 NSTable__tbody__tr--hoverable ${
                                        selectedItem?.id === (row.original as TData & { id: number }).id ? 'active' : ''
                                    }`}
                                >
                                    {row.getVisibleCells().map(cell => {
                                        const columnDef = cell.column.columnDef as ExtendedColumnDef<TData>;
                                        const { alignRight } = columnDef;
                                        return (
                                            <td
                                                className={alignRight ? 'text-right' : ''}
                                                key={cell.id}
                                                onClick={() => onRowClick?.(row.original)}
                                            >
                                                {flexRender(cell.column.columnDef.cell, cell.getContext())}
                                            </td>
                                        );
                                    })}
                                </tr>
                            ))}
                        </tbody>
                    </NSTable>
                </DndContext>
            </div>
        </div>
    );
};

export default forwardRef(DraggableTable) as <TData extends object>(
    props: DraggableTableProps<TData> & { ref?: Ref<DraggableTableRef> },
) => ReturnType<typeof DraggableTable>;
